import _ from "lodash";
import { objectToQueryString } from "./getPageFromLink";

export const concatQueryString = (
  currentSearch: string,
  currentKey?: string,
  currentValue?: any
) => {
  if (!currentSearch) return `${currentKey}=${currentValue}`;

  const params = new URLSearchParams(currentSearch);
  const entries = Array.from(params.entries());
  const resultObj = _.fromPairs(entries);

  if (!currentKey) return objectToQueryString(resultObj);

  resultObj[currentKey] = currentValue;
  return objectToQueryString(resultObj);
};

export const displayTotalStudent = (total: number) => {
  if (!total) return "0";

  if (total > 0 && total <= 1000) return total;

  if (total > 1000 && total <= 10000) return "1k+";

  if (total > 10000 && total <= 100000) return "10k+";

  if (total > 100000 && total <= 1000000) return "100k+";

  return "1M+";
};

export const stripHtml = (html: string) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

export function sanitizeLatexInput(html: string, height?: number): string {
  if (!html) return "";

  // If the input is exactly a raw formula without surrounding text or delimiters, 
  // wrap it with \\( \\) so it's converted to an image.
  // We check if it starts with a common math backslash command (allow wrapping HTML like <p>)
  const cleanText = stripHtml(html).trim();
  let targetHtml = html;

  // Need to be careful: if cleanText is empty, don't do anything
  if (
    cleanText &&
    cleanText.startsWith("\\") &&
    !cleanText.startsWith("\\(") &&
    !cleanText.startsWith("\\[") &&
    !cleanText.startsWith("$$")
  ) {
    // Escape special characters in cleanText before using it in a regex, or just use string replace.
    // Replace only the first occurrence to be safe.
    targetHtml = targetHtml.replace(cleanText, `\\(${cleanText}\\)`);
  }

  const safeEncode = (s: string) => {
    if (!s) return "";
    try {
      return encodeURIComponent(s);
    } catch {
      try {
        const enc =
          typeof TextEncoder !== "undefined" ? new TextEncoder() : null;
        if (enc) {
          const bytes = enc.encode(s);
          return Array.from(bytes)
            .map((b) => "%" + b.toString(16).padStart(2, "0"))
            .join("");
        }
      } catch {
        return Array.from(s)
          .map((ch) => "%" + ch.charCodeAt(0).toString(16).padStart(2, "0"))
          .join("");
      }
      return "";
    }
  };

  // match \( ... \) | \[ ... \] | $$ ... $$
  const mathSegRe = /(\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]|\$\$[\s\S]*?\$\$)/g;

  const processInner = (inner: string) => {
    if (!inner) return "";

    // 1) Decode, clean
    let s = inner
      .replace(/&nbsp;|\u00A0/g, " ")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/[\u200B-\u200D\uFEFF]/g, "");

    // 2) Unicode -> ASCII/macro (TRƯỚC khi xử lý dấu kết hợp)
    s = s
      // Latin math italic -> ASCII
      .replace(/𝑎/g, "a")
      .replace(/𝑏/g, "b")
      .replace(/𝑐/g, "c")
      .replace(/𝑑/g, "d")
      .replace(/𝑒/g, "e")
      .replace(/𝑓/g, "f")
      .replace(/𝑔/g, "g")
      .replace(/𝑖/g, "i")
      .replace(/𝑗/g, "j")
      .replace(/𝑘/g, "k")
      .replace(/𝑚/g, "m")
      .replace(/𝑛/g, "n")
      .replace(/𝑞/g, "q")
      .replace(/𝒒/g, "q")
      .replace(/𝒒̇/g, "\\dot{q}")
      .replace(/𝑟/g, "r")
      .replace(/𝑥/g, "x")
      .replace(/𝑦/g, "y")
      .replace(/𝑧/g, "z")
      .replace(/𝐹/g, "F")
      // Greek -> LaTeX
      .replace(/𝜑/g, "\\varphi")
      .replace(/𝜃/g, "\\theta")
      .replace(/𝜓/g, "\\psi")
      .replace(/𝜂/g, "\\eta")
      .replace(/𝛼/g, "\\alpha")
      .replace(/𝛽/g, "\\beta")
      .replace(/𝛾/g, "\\gamma")
      .replace(/𝛿/g, "\\delta")
      .replace(/𝜆/g, "\\lambda")
      .replace(/𝜇/g, "\\mu")
      .replace(/𝜎/g, "\\sigma")
      .replace(/𝜏/g, "\\tau")
      .replace(/𝜔/g, "\\omega")
      .replace(/Ω/g, "\\Omega")
      .replace(/𝜉/g, "\\xi")
      .replace(/𝜋/g, "\\pi")
      .replace(/𝜌/g, "\\rho")
      .replace(/𝜅/g, "\\kappa")
      .replace(/𝜖/g, "\\epsilon")
      .replace(/𝜗/g, "\\vartheta")
      .replace(/𝜃̇/g, "\\dot{\\theta}");

    // 3) Diaeresis (x ̈) & fallback từng lỗi '#'
    s = s
      .replace(/([A-Za-z])(?:\s|\u00A0|&nbsp;)*\u0308/g, "\\ddot{$1}")
      .replace(/([A-Za-z])#(?!\w)/g, "\\ddot{$1}");

    // 4) Prime: hỗ trợ ’ (U+2019), ′ (U+2032), ' (ASCII)
    //    Làm triple trước, rồi double, rồi single
    s = s
      .replace(/[’]/g, "'") // chuẩn hoá về ASCII để regex đơn giản
      .replace(/([A-Za-z])('{3}|′{3}|’{3})/g, "$1^{\\prime\\prime\\prime}")
      .replace(/([A-Za-z])('{2}|′{2}|’{2})/g, "$1^{\\prime\\prime}")
      .replace(/([A-Za-z])('|′|’)/g, "$1^{\\prime}");

    // 5) Toán tử/kí hiệu + ellipsis
    s = s
      .replace(/–|—/g, "-")
      .replace(/×/g, "\\times")
      .replace(/÷/g, "\\div")
      .replace(/±/g, "\\pm")
      .replace(/∓/g, "\\mp")
      .replace(/∑/g, "\\sum ")
      .replace(/\\sum(?=[A-Za-z])/g, "\\sum ")
      .replace(/∏/g, "\\prod")
      .replace(/∫/g, "\\int")
      .replace(/√/g, "\\sqrt{}")
      .replace(/∞/g, "\\infty")
      .replace(/∂/g, "\\partial")
      .replace(/∇/g, "\\nabla")
      .replace(/≈/g, "\\approx")
      .replace(/≠/g, "\\neq")
      .replace(/≤/g, "\\leq")
      .replace(/≥/g, "\\geq")
      .replace(/≡/g, "\\equiv")
      .replace(/≅/g, "\\cong")
      .replace(/→/g, "\\to")
      .replace(/←/g, "\\leftarrow")
      .replace(/↔/g, "\\leftrightarrow")
      .replace(/⇒/g, "\\Rightarrow")
      .replace(/⇔/g, "\\Leftrightarrow")
      .replace(/∈/g, "\\in")
      .replace(/∉/g, "\\notin")
      .replace(/∅/g, "\\emptyset")
      .replace(/∃/g, "\\exists")
      .replace(/∀/g, "\\forall")
      .replace(/∩/g, "\\cap")
      .replace(/∪/g, "\\cup")
      .replace(/⊂/g, "\\subset")
      .replace(/⊃/g, "\\supset")
      .replace(/⊆/g, "\\subseteq")
      .replace(/⊇/g, "\\supseteq")
      .replace(/∧/g, "\\wedge")
      .replace(/∨/g, "\\vee")
      .replace(/¬/g, "\\neg")
      .replace(/⇒/g, "\\implies")
      .replace(/⇔/g, "\\iff")
      .replace(/\u0307/g, "\\dot{}")
      .replace(/…/g, "\\ldots");

    // 6) Gọn khoảng trắng trước _ ^
    s = s.replace(/\s+_/g, "_").replace(/\s+\^/g, "^");

    // 7) Vá \dot \vec h -> \dot{\vec{h}} (an toàn hơn cho codecogs)
    s = s.replace(/\\dot\s+\\vec\s*([A-Za-z])/g, "\\dot{\\vec{$1}}");

    return s.trim();
  };

  let out = "";
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = mathSegRe.exec(targetHtml)) !== null) {
    out += targetHtml.slice(last, m.index);

    const seg = m[0];
    let inner = seg;
    if (seg.startsWith("\\(") && seg.endsWith("\\)")) inner = seg.slice(2, -2);
    else if (seg.startsWith("\\[") && seg.endsWith("\\]"))
      inner = seg.slice(2, -2);
    else if (seg.startsWith("$$") && seg.endsWith("$"))
      inner = seg.slice(2, -2);

    const processed = processInner(inner);
    if (processed) {
      const encoded = safeEncode(processed);
      const h = height || 24;
      out += `<img src="https://latex.codecogs.com/svg.image?${encoded}" alt="${processed}" style="height:${h}px;vertical-align:middle;" />`;
    }

    last = m.index + seg.length;
  }

  out += targetHtml.slice(last);
  return out;
}

export function getPathnameFromUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);

    return parsedUrl.pathname;
  } catch (error) {
    console.error("Invalid URL:", error);
    return "";
  }
}
